import lookupDataService from '@/services/data/LookupDataService';
import permissionService from '@/services/PermissionService';
import { apiService } from '@/services/ApiService';
import React, { useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import {
  IonContent,
  IonSpinner,
  IonText,
  IonIcon,
  useIonViewWillEnter,
} from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { alertCircleOutline, cloudDownloadOutline, addOutline, gridOutline } from 'ionicons/icons';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle } from '@/contexts/PageTitleContext';
import InspectionModal from '../components/inspection/modal/InspectionModal';
import AddInspectionModal from '@/components/inspection/modal/AddInspectionModal';
import CorrectiveActionModal from '@/components/corrective-action/modal/CorrectiveActionModal';
import { inspectionService } from '../services/InspectionService';
import { auth } from '../services/UserAuthService';
import { 
  InspectionGroup,
  Inspection, 
  InspectionFilters, 
  InspectionLineItem,
  InspectionGridRow
} from '../types/model/inspection';
import { InspectionModalData } from '../types/model/inspection-modal';
import { CAModalData } from '@/types/model/corrective-action';
import { featureFlags } from '@/config/featureFlags';
import { config } from '@/config/config';
import ActionAccordion from '@/components/common/accordion/ActionAccordion';
import PageHeaderBar from '@/components/common/PageHeaderBar';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import InspectionCard from '@/components/inspection/mobile/InspectionCard';
import InspectionAccordionRow from '@/components/inspection/desktop/InspectionAccordionRow';
import InspectingGridView from '@/components/inspection/desktop/InspectingGridView';
import inspectionDataService from '@/services/data/InspectionDataService';
import { offlineModeManager } from '@/services/offline/OfflineModeManager';
import { AddInspectionGroupLineItem } from '@/types/response/InspectionResultResponses';
import { mapInspectionResponseToModalData } from '@/services/util/inspectionModalMapping';
import { ActionAccordionHandlers, ActionAccordionRowData } from '@/types/model/actionAccordion';
import { buildInspectionActionAccordionRows } from '@/services/util/adapters/InspectionActionAccordionAdapter';
import { PageHeaderActionProgress, PageHeaderIconAction } from '@/types/model/pageHeaderBar';
import {
  buildInspectingActionMessage,
  buildInspectingFilterControls,
  buildInspectingFilterSummary,
  mapInspectingControlsToFilters,
} from '@/services/util/adapters/InspectingPageHeaderAdapter';
import { filterInspectionGroupsBySearch } from '@/services/util/inspecting/inspectingSearch';
import { InspectingViewMode, getStoredInspectingViewMode, setStoredInspectingViewMode } from '@/services/util/inspecting/inspectingViewPreference';

const Inspecting: React.FC = () => {
  usePageTitle('Inspecting');

  const [data, setData] = useState<InspectionGroup[]>([]);
  const [viewData, setViewData] = useState<InspectionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [lookupReady, setLookupReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [toastPersistent, setToastPersistent] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<InspectionModalData | null>(null);
  const [isCorrectiveActionModalOpen, setIsCorrectiveActionModalOpen] = useState(false);
  const [correctiveActionModalData, setCorrectiveActionModalData] = useState<CAModalData | null>(null);
  const [cacheProgress, setCacheProgress] = useState({ isCaching: false, progress: 0 });
  const [hasCachedInspections, setHasCachedInspections] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [groupLineItemContentMap, setGroupLineItemContentMap] = React.useState<Record<number, Record<number, ReactNode | null>>>({});
  const [actionAccordionRows, setActionAccordionRows] = useState<ActionAccordionRowData[]>([]);
  const actionAccordionHandlersRef = useRef<ActionAccordionHandlers | null>(null);
  const contentMapInitializedRef = React.useRef(false);
  const [expandedGroupValues, setExpandedGroupValues] = useState<string[]>([]);
  const [expandedLineItemValues, setExpandedLineItemValues] = useState<Record<number, string[]>>({});
  const autoExpandedRef = React.useRef(false);
  const showOfflineManagement = featureFlags.isOfflineManagementEnabled();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<InspectingViewMode>(() => getStoredInspectingViewMode());
  const [inspectionGridRows, setInspectionGridRows] = useState<InspectionGridRow[]>([]);
  const [gridRowsLoading, setGridRowsLoading] = useState(false);

  const groupMap = lookupDataService.getGroups();
  const siteMap = lookupDataService.getSites();
  const inspectorMap = lookupDataService.getInspectors();
  const globalFiles = lookupDataService.getExistingResources() || [];
  const currentUserId = parseInt(auth.getUserDetails()?.userId || '0', 10) || null;

  const canAddInspection = permissionService.canMarkAsComplete();
  const canEditOtherInspectors = permissionService.hasRole('EditInspectionResults');

  const groups = groupMap ? Array.from(groupMap.values()).map(g => ({ id: g.Id, name: g.Name })) : [];
  const sites = siteMap ? Array.from(siteMap.values()).map(s => ({ id: s.Id, name: s.Name })) : [];
  let inspectors = inspectorMap ? Array.from(inspectorMap.values()).map(i => ({ id: i.Id, name: i.Name })) : [];
  if (!canEditOtherInspectors) {
    inspectors = inspectors.filter(i => i.id === currentUserId);
  }
 
  const getLineItems = async (groupId: number) : Promise<AddInspectionGroupLineItem[]> => {
    return await apiService.getInspectionResultLineItems(groupId);
  };

  const [isOfflineModeEnabled, setIsOfflineModeEnabled] = useState(
    () => offlineModeManager.getCurrentStatus().isOfflineMode
  );

  useEffect(() => {
    return offlineModeManager.addListener((status) => {
      setIsOfflineModeEnabled(status.isOfflineMode);
    });
  }, []);
  
  const [filters, setFilters] = useState<InspectionFilters>({
    showAll: false,
    selectedStatuses: ['not-started', 'in-progress'],
  });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const ensureLookupReady = async (): Promise<boolean> => {
    try {
      setLookupReady(false);
      await lookupDataService.initialize();
      const ready = lookupDataService.isLoaded();
      setLookupReady(ready);
      return ready;
    } catch (error) {
      console.error('Inspecting page lookup initialization failed:', error);
      setLookupReady(false);
      return false;
    }
  };

  const loadInspectionData = async () => {
    if (!(await ensureLookupReady())) {
      setError('Lookup data is still loading. Please try again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      contentMapInitializedRef.current = false;
      autoExpandedRef.current = false;
      setExpandedGroupValues([]);
      setExpandedLineItemValues({});
      const userPreferences = lookupDataService.getUserPreferences();

      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(fromDate.getDate() - config.Inspecting.pastDaysToShow);
      const inspectionData = await inspectionService.getGroupedInspectionData(
        userPreferences?.GroupIds ?? [],
        userPreferences?.SiteIds ?? [],
        userPreferences?.AreaIds ?? [],
        fromDate.toISOString().slice(0, 10),
        today.toISOString().slice(0, 10)
      );
      setData(inspectionData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load inspection data';
      setError(errorMessage);
      showPageToast(errorMessage, 'error');
      console.error('Error loading inspection data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterData = async () => {
      try {
        const filteredData = filters.showAll
          ? data
          : await inspectionService.filterInspectionGroups(data, filters);

        const filteredDataWithNonEmptyLineItems = filters.showAll
          ? filteredData
          : filteredData.map(group => ({
              ...group,
              lineItems: group.lineItems?.filter(li => (li.subItems?.length ?? 0) > 0) ?? null
            }));

        const searchedData = filterInspectionGroupsBySearch(filteredDataWithNonEmptyLineItems, searchQuery);

        setViewData(searchedData);
        
        if (searchedData.length === 0) {
          setError('No inspection results found matching the current filters and search.');
        } else {
          setError(null);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to filter inspection data';
        setError(errorMessage);
        showPageToast(errorMessage, 'error');
        console.error('Error filtering inspection data:', err);
      }
    };
    filterData();
  }, [filters, data, searchQuery]);

  // Set content for inspections that are already loaded (e.g. from cache) when data first loads
  useEffect(() => {
    if (contentMapInitializedRef.current || data.length === 0) return;
    contentMapInitializedRef.current = true;
    const initializeContentMap = async () => {
      const newMap: Record<number, Record<number, ReactNode | null>> = {};
      for (const group of data) {
        if (group.lineItems) {
          for (const lineItem of group.lineItems) {
            if (lineItem.inspections && lineItem.inspections.length > 0) {
              newMap[group.id] = newMap[group.id] || {};
              newMap[group.id][lineItem.id] = getContentForInspections(lineItem.inspections);
            }
          }
        }
      }
      setGroupLineItemContentMap(newMap);
    };
    initializeContentMap();
  }, [data]);

  // Auto-expand all groups and line items when user preferences have all three filters set
  useEffect(() => {
    if (autoExpandedRef.current || viewData.length === 0) return;

    const userPreferences = lookupDataService.getUserPreferences();
    const shouldAutoExpand =
      (userPreferences?.GroupIds?.length ?? 0) > 0 &&
      (userPreferences?.SiteIds?.length ?? 0) > 0 &&
      (userPreferences?.AreaIds?.length ?? 0) > 0;

    if (!shouldAutoExpand) return;

    autoExpandedRef.current = true;

    setExpandedGroupValues(viewData.map((_, gi) => gi.toString()));

    const lineItemVals: Record<number, string[]> = {};
    for (const group of viewData) {
      if (group.lineItems?.length) {
        lineItemVals[group.id] = group.lineItems.map(li => li.id.toString());
      }
    }
    setExpandedLineItemValues(lineItemVals);

    // Trigger data loading for all auto-expanded line items
    for (const group of viewData) {
      if (group.lineItems) {
        for (const lineItem of group.lineItems) {
          handleLineItemExpand(lineItem);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewData]);

  useEffect(() => {
    // Whenever view mode or filtered data changes, refresh loaded line item content so mobile/desktop content stays in sync.
    const updateContentMapForViewData = () => {
      const newMap = { ...groupLineItemContentMap };
      for (const group of viewData) {
        if (group.lineItems) {
          for (const lineItem of group.lineItems) {
            if (groupLineItemContentMap[group.id]?.[lineItem.id]) {
              console.log(`Updating content map for group ${group.id} - ${group.title}, line item ${lineItem.id} - ${lineItem.lineItem}`);
              refreshContentForLineItem(lineItem, true); // Force refresh to ensure content is up to date with any changes
            }
          }
        }
      }
      setGroupLineItemContentMap(newMap);
    };
    updateContentMapForViewData();
  }, [viewData]);

  useEffect(() => {
    setActionAccordionRows((prevRows) =>
      buildInspectionActionAccordionRows({
        groups: viewData,
        groupLineItemContentMap,
        expandedGroups: expandedGroupValues,
        expandedLineItems: expandedLineItemValues,
        previousRows: prevRows,
      })
    );
  }, [viewData, groupLineItemContentMap, expandedGroupValues, expandedLineItemValues]);

  const checkCachedInspections = async () => {
    try {
      const cached = await inspectionDataService.getCachedContractInspectionGroups();
      setHasCachedInspections(cached.length > 0);
    } catch {
      setHasCachedInspections(false);
    }
  };

  useIonViewWillEnter(() => {
    void loadInspectionData();
    void checkCachedInspections();
  });

  const showPageToast = (
    message: string,
    variant: ToasterVariant = 'success',
    autoDismiss: boolean = true,
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastPersistent(!autoDismiss);
    setShowToast(true);
  };

  const handleCacheAllData = async () => {
    setCacheProgress({ isCaching: true, progress: 0 });
    try {
      // Cache all lookup data
      setCacheProgress({ isCaching: true, progress: 10 });

      const updatedData = await inspectionService.getInspectionForGroups(data);
      
      setCacheProgress({ isCaching: true, progress: 50 });
      await inspectionService.persistEssentialData(updatedData);
      
      setCacheProgress({ isCaching: true, progress: 100 });
      // await loadTableStats();
      
      await checkCachedInspections();
      showPageToast('All data cached successfully', 'success');
    } catch (error) {
      console.error('Error caching data:', error);
      showPageToast('Failed to cache data', 'error');
    } finally {
      setCacheProgress({ isCaching: false, progress: 0 });
    }
  };

  const handleInspectionClick = async (inspection: Inspection) => {
    setLoadingModal(true);
    try {
      const inspectionId = inspection.inspectionResultId;
      const [inspectionResponse, filesResponse] = await Promise.all([
        inspectionService.getContractInspection(inspectionId),
        inspectionService.getContractExistingResources(),
      ]);

      const inspectionModalData = mapInspectionResponseToModalData(
        inspectionResponse,
        filesResponse,
        inspection,
      );

      setModalData(inspectionModalData);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching inspection data:', err);
      setError('Failed to load inspection details');
      showPageToast('Failed to load inspection details', 'error');
    } finally {
      setLoadingModal(false);
    }
  };

  const filterControls = buildInspectingFilterControls(filters);
  const inspectingActionProgress: PageHeaderActionProgress | undefined = cacheProgress.isCaching
    ? { mode: 'determinate', value: cacheProgress.progress }
    : undefined;
  const canToggleView = !isMobile && !Capacitor.isNativePlatform();
  const effectiveViewMode: InspectingViewMode = canToggleView ? viewMode : 'accordion';

  const handleViewModeChange = (mode: InspectingViewMode) => {
    setViewMode(mode);
    setStoredInspectingViewMode(mode);
  };

  useEffect(() => {
    if (effectiveViewMode !== 'grid') return;

    let cancelled = false;
    const loadGridRows = async () => {
      setGridRowsLoading(true);
      try {
        const rows = await inspectionService.getFlattenedInspectionItems(data);
        if (!cancelled) setInspectionGridRows(rows);
      } catch (err) {
        console.error('Error loading inspection grid rows:', err);
        if (!cancelled) showPageToast('Failed to load grid data', 'error');
      } finally {
        if (!cancelled) setGridRowsLoading(false);
      }
    };

    void loadGridRows();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveViewMode, data]);

  const actionIcons: PageHeaderIconAction[] = [
    ...(!isOfflineModeEnabled && canAddInspection
      ? [{
          kind: 'icon' as const,
          group: 1,
          order: 1,
          id: 'inspecting-new-inspection',
          label: 'New Inspection',
          icon: addOutline,
          requiresConfirm: false,
          onExecute: () => setIsAddModalOpen(true),
        }]
      : []),
    ...(showOfflineManagement && !isOfflineModeEnabled
      ? [{
          kind: 'icon' as const,
          group: 1,
          order: 2,
          id: 'inspecting-download-offline-data',
          label: 'Download Offline Data',
          icon: cloudDownloadOutline,
          disabled: cacheProgress.isCaching || hasCachedInspections,
          onExecute: handleCacheAllData,
        }]
      : []),
    ...(canToggleView
      ? [{
          kind: 'icon' as const,
          group: 2,
          order: 1,
          id: 'inspecting-switch-to-grid',
          label: 'Switch to Grid View',
          icon: gridOutline,
          requiresConfirm: false,
          onExecute: () => handleViewModeChange('grid'),
        }]
      : []),
  ];

  const handleAddCorrectiveActionFromInspection = (inspectionContext: InspectionModalData) => {
    const group = data.find(currentGroup =>
      currentGroup.lineItems?.some(lineItem => lineItem.id === inspectionContext.lineItemId),
    );
    const lineItem = group?.lineItems?.find(currentLineItem => currentLineItem.id === inspectionContext.lineItemId);

    if (!group || !lineItem) {
      showPageToast('Unable to determine group and line item for corrective action.', 'error');
      return;
    }

    setIsModalOpen(false);
    setModalData(null);

    setCorrectiveActionModalData({
      isNew: true,
      groupId: group.id,
      groupIdentifier: group.groupIdentifier,
      groupTitle: group.title,
      lineItemId: lineItem.id,
      lineItem: lineItem.lineItem,
      lineItemTitle: lineItem.title,
      inspectionResultId: inspectionContext.inspectionResultId,
      inspectionDate: inspectionContext.inspectionDate,
      existingResources: globalFiles,
    });
    setIsCorrectiveActionModalOpen(true);
  };

  const handleCorrectiveActionModalClose = () => {
    setIsCorrectiveActionModalOpen(false);
    setCorrectiveActionModalData(null);
  };

  const handleCorrectiveActionModalSaved = () => {
    setIsCorrectiveActionModalOpen(false);
    setCorrectiveActionModalData(null);
    showPageToast(
      'Corrective action has been created and can be viewed on Corrective Actions page.',
      'success',
      false,
    );
  };

  const getContentForInspections = (inspections: Inspection[]): ReactNode => {

    if (inspections.length === 0) {
        return <IonText color="medium">No inspections found.</IonText>;
    }

    return isMobile ? (
      <div style={{ padding: '1rem' }}>
        {inspections.map((inspection) => (
          <InspectionCard
            key={inspection.inspectionResultId}
            inspection={inspection}
            onInspectionClick={handleInspectionClick}
          />
        ))}
      </div>
    ) : (
      <div style={{ width: '100%' }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 100px',
          gap: '1rem',
          padding: '1rem',
          background: 'var(--ion-color-step-150, var(--ion-color-light-tint))',
          color: 'var(--ion-text-color, inherit)',
          fontWeight: '600',
          borderBottom: '1px solid var(--ion-color-step-250, var(--ion-color-light-shade))'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Site</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Scheduled On</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Inspected On</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Status</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>Due</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>Actions</div>
        </div>
        
        {/* Table Rows */}
        {inspections.map((inspection, index) => (
          <InspectionAccordionRow
            key={inspection.inspectionResultId}
            inspection={inspection}
            index={index}
            isDetailsExpanded={false}
            onInspectionClick={handleInspectionClick}
            onToggleDetails={() => {}}
          />
        ))}
      </div>
    );
  };
  
  const handleLineItemExpand = async (lineItem: InspectionLineItem) => {
      refreshContentForLineItem(lineItem);
  };

  const handleActionAccordionRowExpand = async (row: ActionAccordionRowData) => {
    if (row.type !== 'LineItem') {
      return;
    }

    for (const group of viewData) {
      const lineItem = group.lineItems?.find((candidate) => candidate.id === row.id);
      if (lineItem) {
        await handleLineItemExpand(lineItem);
        break;
      }
    }
  };
  
  const refreshContentForLineItem = async (lineItem: InspectionLineItem, force: boolean = false) => {
    try {
      const groupId = lineItem.groupId;
      const lineItemId = lineItem.id;
      const group = data.find(g => g.id === groupId);

      if (!group || !lineItem) {
        return;
      }

      let inspectionsToRender: Inspection[] = [];

      if (lineItem.inspections && !force) {
        inspectionsToRender = lineItem.inspections;
      } else {
        inspectionsToRender = await inspectionService.getInspections(lineItem.subItems?.map(sub => sub.id) ?? []);
        inspectionsToRender.forEach(inspection => {
          const subItem = lineItem.subItems?.find(sub => sub.id === inspection.inspectionResultId);
          if (subItem) {
            inspection.status = subItem.status;
            inspection.timeSensitivity = subItem.timeSensitivity;
            inspection.scheduledDate = subItem.scheduledDate;
          }
        });
      }

      const content = getContentForInspections(inspectionsToRender);
      setGroupLineItemContentMap(prev => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          [lineItemId]: content,
        }
      }));
    } catch (err) {
      console.error('Error loading inspections for line item:', err);
    }
  };

  const handleModalDataUpdate = (updatedData: InspectionModalData) => {
    // Update the main data with the changes
    setData(prevData => 
      prevData.map(group => ({
        ...group,
        lineItems: group.lineItems?.map(li => ({
          ...li,
          inspections: li.inspections?.map(inspection => 
            inspection.inspectionResultId === updatedData.inspectionResultId 
              ? { ...inspection }
              : inspection
          ) ?? null
        })) ?? null
      }))
    );
  };

  const handleInspectionDeleted = () => {
    setIsModalOpen(false);
    setModalData(null);
    loadInspectionData();
  };

  const handleActionAccordionHandlersReady = useCallback((handlers: ActionAccordionHandlers) => {
    actionAccordionHandlersRef.current = handlers;
  }, []);

  const renderContent = () => {
    if (!lookupReady || loading) {
      return (
        <div className="loading-state">
          <IonSpinner name="crescent" />
          <IonText color="medium">
            <p>{lookupReady ? 'Loading inspection results...' : 'Initializing lookup data...'}</p>
          </IonText>
        </div>
      );
    }

    if (error && viewData.length === 0) {
      return (
        <div className="empty-state">
          <IonIcon icon={alertCircleOutline} />
          <IonText color="medium">
            <h3>No Results Found</h3>
            <p>{error}</p>
          </IonText>
        </div>
      );
    }

    return (
      <ActionAccordion
        rows={actionAccordionRows}
        onRowsChange={setActionAccordionRows}
        onHandlersReady={handleActionAccordionHandlersReady}
        onRowExpand={handleActionAccordionRowExpand}
      />
    );
  };

  return (
    <PageLayout>
      
      <IonContent fullscreen className="ion-padding">
        <div className="inspecting-container">
          {effectiveViewMode === 'grid' ? (
            <InspectingGridView
              rows={inspectionGridRows}
              loading={gridRowsLoading}
              canAddNew={!isOfflineModeEnabled && canAddInspection}
              onEdit={handleInspectionClick}
              onAddNew={() => setIsAddModalOpen(true)}
              onSwitchToAccordion={() => handleViewModeChange('accordion')}
            />
          ) : (
            <>
              <PageHeaderBar
                filters={{
                  controls: filterControls,
                  summaryText: buildInspectingFilterSummary(filters),
                  onApply: (nextControls) => {
                    setFilters(mapInspectingControlsToFilters(nextControls, filters));
                  },
                }}
                actions={{
                  iconActions: actionIcons,
                  message: buildInspectingActionMessage(
                    cacheProgress.isCaching,
                    cacheProgress.progress,
                    hasCachedInspections
                  ),
                  runningActionId: cacheProgress.isCaching ? 'inspecting-download-offline-data' : null,
                  runningActionProgress: inspectingActionProgress,
                }}
                search={{
                  value: searchQuery,
                  placeholder: 'Search groups and line items',
                  debounceMs: 300,
                  onSearch: setSearchQuery,
                }}
                collapsible={isMobile}
              />

              <div className="inspection-results">
                {renderContent()}
              </div>
            </>
          )}
        </div>
        
        <AddInspectionModal
          isOpen={isAddModalOpen}
          onDidDismiss={() => setIsAddModalOpen(false)}
          onSuccess={() => {
            setIsAddModalOpen(false);
            loadInspectionData();
          }}
          groups={groups}
          inspectors={inspectors}
          sites={sites}
          getLineItems={getLineItems}
          globalFiles={globalFiles}
          currentUserId={currentUserId}
          canEditOtherInspectors={canEditOtherInspectors}
        />

        <InspectionModal
          isOpen={isModalOpen}
          onDidDismiss={() => setIsModalOpen(false)}
          inspectionData={modalData}
          onDataUpdate={handleModalDataUpdate}
          onDeleted={handleInspectionDeleted}
          onAddCorrectiveAction={handleAddCorrectiveActionFromInspection}
        />

        <CorrectiveActionModal
          isOpen={isCorrectiveActionModalOpen}
          modalData={correctiveActionModalData}
          onClose={handleCorrectiveActionModalClose}
          onSaved={handleCorrectiveActionModalSaved}
        />

        {loadingModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.4)',
            zIndex: 9999,
          }}>
            <IonSpinner name="crescent" style={{ width: '48px', height: '48px', color: 'white' }} />
          </div>
        )}

        <Toaster
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage || error || 'An error occurred'}
          variant={toastVariant}
          persistent={toastPersistent}
        />
      </IonContent>
    </PageLayout>
  );
};

export default Inspecting;